import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcessFlowchartSchemaMapperSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  ProcessEdgeForValidation,
  ProcessFlowchartForValidation,
  ProcessNodeForValidation,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  FieldDefinitionGraphQL,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {assertNever} from 'util/Types';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import buildSchemaMapperConfigsFromStaticData from './buildSchemaMapperConfigFromStaticData';
import {generateDeterministicEdgeId} from './buildStaticDataFromFlowchartConfigGraphQL';
import {
  EndStaticNodeData,
  isCollapsedStaticNodeData,
  isStaticTaskSchemaNodeData,
  RouterStaticNodeData,
  StaticFlowchartNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaDomainData,
} from './processFlowchartConfigEditorNodeTypes';
import {isLookupConfiguredForDataFlowEdge} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';
import {getDefaultTransitionSource} from './TransitionSourceUtils';
import {getTransitionSourceForNodeIdPair} from './types/transition/transitionAdapter';
interface ValidationFlowchartData {
  flowchart: ProcessFlowchartForValidation;
  designSpace: ProcessFlowchartDesignSpace;
}

function isFieldNotArchived(field: FieldDefinitionGraphQL): boolean {
  return !field.fieldDefinition?.archiveData?.archived;
}

/**
 * Archived fields can't be (re)mapped by users, so they should never trigger validation errors.
 * Stripping them out here — as the flowchart is built for validation — keeps every downstream
 * validation rule from having to special-case archived fields.
 */
function stripArchivedFieldDefinitions(
  spec: TaskDefinitionVersionSpecWithTaskSchema
): TaskDefinitionVersionSpecWithTaskSchema {
  // Slim viewer-query task definition versions omit field definitions entirely. Treat missing definitions
  // as empty so non-field validation can still run while field-level validation naturally no-ops.
  return {
    ...spec,
    inputFieldDefinitions: (spec.inputFieldDefinitions ?? []).filter((field) => isFieldNotArchived(field)),
    outputFieldDefinitions: spec.outputFieldDefinitions?.filter((field) => isFieldNotArchived(field)) ?? null,
  };
}

interface BuildValidationFlowchartFromStaticDataRunnerOptions extends Omit<
  FlowchartConfigMetadata,
  'interactionState'
> {}

interface BuildValidationFlowchartFromStaticDataRunnerArgs {
  id: string;
  staticNodesData: Array<StaticFlowchartNodeData>;
  staticEdgesData: Array<FlowchartStaticEdgeData>;
  options: BuildValidationFlowchartFromStaticDataRunnerOptions;
}

class BuildValidationFlowchartFromStaticDataRunner {
  private readonly id: string;
  private readonly staticNodesData: Array<StaticFlowchartNodeData>;
  private readonly staticEdgesData: Array<FlowchartStaticEdgeData>;
  private readonly options: BuildValidationFlowchartFromStaticDataRunnerOptions;

  private rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  private inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
  private nodes: Array<ProcessNodeForValidation>;
  private edges: Array<ProcessEdgeForValidation>;
  private designSpace: ProcessFlowchartDesignSpace | null;

  constructor(args: BuildValidationFlowchartFromStaticDataRunnerArgs) {
    this.id = args.id;
    this.staticNodesData = args.staticNodesData;
    this.staticEdgesData = args.staticEdgesData;
    this.options = args.options;
    assert(this.options.rootTaskSchema != null, 'Expected root task schema to be defined');
    this.rootTaskSchema = this.options.rootTaskSchema;
    this.inboundSchemaMapperConfigsByNodeId = {};
    this.nodes = [];
    this.edges = [];
    this.designSpace = null;
  }

  run(): ValidationFlowchartData {
    this.buildSchemaMapperConfigs();
    this.convertStaticDataToNodes();
    this.convertStaticDataToEdges();
    this.buildDesignSpace();
    assert(this.designSpace != null, 'Design space is required');
    return {
      flowchart: {
        id: this.id,
        nodes: this.nodes,
        edges: this.edges,
        externalRootNodeStaticData:
          this.options.rootNodeStaticData &&
          this.buildProcessNodeForValidationFromStaticNodeData(this.options.rootNodeStaticData),
      },
      designSpace: this.designSpace,
    };
  }

  private buildSchemaMapperConfigs(): void {
    const {inboundSchemaMapperConfigsByNodeId} = buildSchemaMapperConfigsFromStaticData({
      nodes: this.staticNodesData,
      edges: this.staticEdgesData,
      transitions: this.options.transitions,
      rootNodeStaticData: this.options.rootNodeStaticData,
    });
    this.inboundSchemaMapperConfigsByNodeId = inboundSchemaMapperConfigsByNodeId;
  }

  private buildProcessNodeForValidationFromStaticNodeData(
    staticNodeData: EndStaticNodeData | RouterStaticNodeData | TaskSchemaDomainData
  ): ProcessNodeForValidation {
    // Note: Field definitions should be properly populated from taskDefinitionVersionSpec
    // If validation issues persist, check that the GraphQL data includes field definitions

    return {
      ...staticNodeData,
      name: staticNodeData.nodeName,
      nodeType: staticNodeData.nodeDetailsType,
      taskDefinitionVersionSpec: isStaticTaskSchemaNodeData(staticNodeData)
        ? stripArchivedFieldDefinitions(staticNodeData.taskDefinitionVersionSpec)
        : undefined,
      executionType: isStaticTaskSchemaNodeData(staticNodeData)
        ? staticNodeData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
        : undefined,
    } as ProcessNodeForValidation;
  }

  private convertStaticDataToNodes(): void {
    this.nodes = this.staticNodesData
      .filter(
        (staticNodeData): staticNodeData is EndStaticNodeData | RouterStaticNodeData | TaskSchemaDomainData =>
          staticNodeData.nodeType !== ProcessFlowchartNodeType.PLACEHOLDER_NODE &&
          !isCollapsedStaticNodeData(staticNodeData)
      )
      .map((staticNodeData) => this.buildProcessNodeForValidationFromStaticNodeData(staticNodeData));
  }

  private convertStaticDataToEdges(): void {
    const placeholderNodeIds = new Set(
      this.staticNodesData
        .filter((node) => node.nodeType === ProcessFlowchartNodeType.PLACEHOLDER_NODE)
        .map((node) => node.id)
    );

    // Build a set of valid node IDs to filter out orphaned edges (edges referencing deleted nodes)
    const validNodeIds = new Set(this.staticNodesData.map((node) => node.id));

    this.edges = this.staticEdgesData
      .filter(
        (staticEdgeData) =>
          !placeholderNodeIds.has(staticEdgeData.source) &&
          !placeholderNodeIds.has(staticEdgeData.target) &&
          // Filter out edges that reference nodes that no longer exist (orphaned edges)
          validNodeIds.has(staticEdgeData.source) &&
          validNodeIds.has(staticEdgeData.target)
      )
      .map((staticEdgeData): ProcessEdgeForValidation => {
        if (staticEdgeData.type === ProcessFlowchartEdgeType.DATA_FLOW) {
          return {
            ...staticEdgeData,
            isLookupConfigured: isLookupConfiguredForDataFlowEdge(
              staticEdgeData,
              this.options.transitions,
              this.staticNodesData
            ),
            transitionSource:
              getTransitionSourceForNodeIdPair(
                staticEdgeData.source,
                staticEdgeData.target,
                this.options.transitions,
                this.staticNodesData
              ) ?? getDefaultTransitionSource(staticEdgeData.source, this.staticNodesData),
          };
        } else if (staticEdgeData.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
          return {
            ...staticEdgeData,
            type: ProcessFlowchartEdgeType.CONTROL_FLOW,
          };
        } else {
          throw new Error(`Unexpected edge type: ${assertNever(staticEdgeData.type)}`);
        }
      });
  }

  private buildDesignSpace(): void {
    // Constructs available nodes and edges based on currently placed nodes and data flow edges
    // Get a map of task schema IDs to nodes that implement that task schema
    const schemaIdToNodeIds = new Map<string, Array<string>>();
    for (const node of this.staticNodesData) {
      if (node.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
        schemaIdToNodeIds.set(node.taskSchemaId, [
          ...(schemaIdToNodeIds.get(node.taskSchemaId) ?? []),
          node.id,
        ]);
      }
    }
    const nodeIdToSchemaId = new Map<string, string>();
    for (const [schemaId, nodeIds] of schemaIdToNodeIds.entries()) {
      for (const nodeId of nodeIds) {
        nodeIdToSchemaId.set(nodeId, schemaId);
      }
    }

    // Get a map of data flow edge source to targets so we can construct available control flow edges
    const dataFlowEdgeSourceToTargetsMap = new Map<string, Array<string>>();
    for (const edge of this.staticEdgesData) {
      if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        dataFlowEdgeSourceToTargetsMap.set(edge.source, [
          ...(dataFlowEdgeSourceToTargetsMap.get(edge.source) ?? []),
          edge.target,
        ]);
      }
    }

    this.designSpace = {
      // Available nodes are based on task schemas that have been placed in the flowchart.
      // Their inbound schema mappers are based on data flow edges that have been drawn.
      availableNodes: Array.from(schemaIdToNodeIds.entries()).map(([schemaId, nodeIds]) => ({
        clientId: schemaId,
        schemaId,
        name: schemaId,
        inboundMapperConfigs: nodeIds
          .map(
            (nodeId) =>
              this.inboundSchemaMapperConfigsByNodeId[nodeId]?.map((config) => ({
                clientId: config.schemaMapperConfigClientId ?? uuidv4(),
                fromTaskSchemaIds: config.schemaMapperDependencies.map(
                  (source) => source.fromNodeConfigClientId
                ),
                toTaskSchemaId: config.toNodeConfigClientId,
              })) ?? []
          )
          .flat(),
      })),

      // Available edges are based on data flow edges that have been drawn.
      availableEdges: Array.from(dataFlowEdgeSourceToTargetsMap.entries()).flatMap(([source, targets]) =>
        targets.map((target) => ({
          clientId: generateDeterministicEdgeId(source, target),
          fromNodeId: source,
          toNodeId: target,
        }))
      ),

      flowchartSchema: this.rootTaskSchema,

      flowchartConfig: {
        availableTaskSchemas: Array.from(schemaIdToNodeIds.keys()).map((schemaId) => ({
          id: schemaId,
          // Schema name is not used in validation, so we don't need to actually set it to something - could be cleaned up
          name: schemaId,
        })),
        // We do separate validation when useHorizontalFlowchart is true
        schemaMapperConfigs: [],
      },
    };
  }
}

export default function buildValidationFlowchartFromStaticData(
  args: BuildValidationFlowchartFromStaticDataRunnerArgs
): ValidationFlowchartData {
  const runner = new BuildValidationFlowchartFromStaticDataRunner(args);
  return runner.run();
}
